import React, { useState } from 'react';
import { useClientContactsByClient, createClientContact, deleteClientContact, ClientContact, CreateClientContactData } from '../useData';
import { Link } from 'react-router-dom';
import { Button, ConfirmationDialog } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';

interface ClientContactsSectionProps {
  clientId: number;
}

const ClientContactsSection: React.FC<ClientContactsSectionProps> = ({ clientId }) => {
  const { showSuccess, showError } = useNotification();
  const { data: contacts, isLoading, mutate } = useClientContactsByClient(clientId);
  const [showAddForm, setShowAddForm] = useState(false);
  const [contactToDeleteId, setContactToDeleteId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setContactToDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = contactToDeleteId;
    if (id == null) return;
    setContactToDeleteId(null);
    try {
      await deleteClientContact(id);
      showSuccess('Contact deleted successfully');
      mutate();
    } catch (error: any) {
      showError(error?.message || 'Failed to delete contact');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Contacts</h3>
        <p className="text-gray-500 text-sm">Loading contacts...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">
          Contacts ({contacts.length})
        </h3>
        <Link to={`/client-contacts/new?clientId=${clientId}`}>
          <Button variant="default" size="sm">
            Add New Contact
          </Button>
        </Link>
      </div>

      {contacts.length === 0 ? (
        <p className="text-gray-500 text-sm">No contacts found for this client.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => {
            const name = `${contact.clientContactFirstName || ''} ${contact.clientContactLastName || ''}`.trim();
            return (
              <div
                key={contact.clientContactId}
                className="border border-gray-200 rounded p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Link
                      to={`/client-contacts/${contact.clientContactId}`}
                      className="font-medium text-indigo-600 hover:underline"
                    >
                      {name || `Contact #${contact.clientContactId}`}
                    </Link>
                    {contact.clientContactPosition && (
                      <p className="text-sm text-gray-600 mt-1">{contact.clientContactPosition}</p>
                    )}
                    <div className="mt-2 space-y-1">
                      {contact.clientContactEmail && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Email:</span>{' '}
                          <a
                            href={`mailto:${contact.clientContactEmail}`}
                            className="text-indigo-600 hover:underline"
                          >
                            {contact.clientContactEmail}
                          </a>
                        </p>
                      )}
                      {contact.clientContactPhone && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Phone:</span>{' '}
                          <a
                            href={`tel:${contact.clientContactPhone}`}
                            className="text-indigo-600 hover:underline"
                          >
                            {contact.clientContactPhone}
                          </a>
                        </p>
                      )}
                    </div>
                    {contact.clientContactNotes && (
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                        {contact.clientContactNotes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Link
                      to={`/client-contacts/${contact.clientContactId}/edit`}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      onClick={() => handleDeleteClick(contact.clientContactId)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationDialog
        isOpen={contactToDeleteId !== null}
        onClose={() => setContactToDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete contact"
        message="Are you sure you want to delete this contact?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default ClientContactsSection;
